import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { IMonthlyOptionColumn, IMonthlyOptionReport, IMonthlyOptionRow } from '../../interfaces/monthly-option-report.interface';
import { IOptionsCalculateRowPayload } from '../../interfaces/options-calculate-row.interface';
import { OptionsApprovalService } from '../../services/options-approval.service';
import { MapsCalculationService } from '../../services/maps-calculation.service';

@Component({
    selector: 'lynkd-pattern-options-monthly-table-bottom',
    templateUrl: './options-monthly-table-bottom.component.html',
    styleUrls: ['./options-monthly-table-bottom.component.scss']
})
export class OptionsMonthlyTableBottomComponent implements OnInit, OnChanges {
    @Input()
    public attributeValue: string;
    @Input()
    public data: IMonthlyOptionReport;

    @Input()
    public timeValue: string;

    @Input()
    public loading: boolean;

    @Output()
    public readonly edit: EventEmitter<Array<IOptionsCalculateRowPayload>> = new EventEmitter();

    public previousData: IMonthlyOptionReport;

    public editId: { [key: string]: number } | null = null;

    public constructor(
        private readonly _optionsApprovalService: OptionsApprovalService,
        public readonly mapsCalculationService: MapsCalculationService
    ) {}

    public ngOnInit(): void {
        this.initializeProductLevelSkus();
        this.updateTotalRow();
    }

    public editable(column: IMonthlyOptionColumn, dataId: number): boolean {
        if (this.attributeValue === 'ALL') {
            return false;
        }
        if (this.editId === null || this.editId === undefined) {
            return false;
        }
        return this.editId[column.column_name] === dataId;
    }

    public skuCountClass(): string {
        if (this.attributeValue === 'ALL') {
            return 'readonly-column';
        }
        return 'editable-cell';
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.data) {
            this.previousData = changes.data.previousValue;
            this.initializeProductLevelSkus();
            this.updateTotalRow();

            if (this.data) {
                // Trigger an update of the tYOptionsApprovalData in the Plans Component
                // whenever the sku plans are edited
                this.edit.emit(this.getEditedRows());
            }
        }
    }

    public startEdit(columnName: string, rowNumber: number): void {
        this.editId = { [columnName]: rowNumber };
    }

    public stopEdit(): void {
        this.edit.emit(this.getEditedRows());
        this.updateTotalRow();
        this.editId = null;
    }

    public getEditedRows(): Array<IOptionsCalculateRowPayload> {
        const payload: Array<IOptionsCalculateRowPayload> = [];

        // Take slice of rows excluding the total row
        for (const row of this.data.rows.slice(1)) {
            for (const [key, value] of Object.entries(row.sku_plans)) {
                if (!['Total', 'sku_plan'].includes(key))
                    payload.push({
                        attributeValueId: row.attributeValueId,
                        optionId: row.optionId,
                        sku_plan: +value,
                        product_level_value: row.product_level_value,
                        attribute_value: this.data.attribute_value,
                        time_value: this.timeValue,
                        sub_time_value: key
                    });
            }
        }

        return payload;
    }

    public checkSkuPlanTotalMismatch(skuPlans: Record<string, number>, total: number): boolean {
        const skuPlanValues: Array<number> = Object.values(skuPlans).splice(0, Object.values(skuPlans).length - 2);
        const skuPlanSum: number = skuPlanValues.reduce((skuOne: number, skuTwo: number) => skuOne + skuTwo, 0);

        return Number(skuPlanSum) !== Number(total);
    }

    public calculateGrandVariance(rows: Array<IMonthlyOptionRow>): number {
        if (!rows) {
            return 0;
        }

        // Get the total of all sku_plan values
        const totalSkuPlan: number = rows.reduce((total: number, row: IMonthlyOptionRow) => {
            if (row.product_level_value.toLowerCase() !== 'total') {
                return total + (row.sku_plans.sku_plan || 0);
            }
            return total;
        }, 0);

        // Get the sum of all monthly values for all rows
        const totalMonthlyValues: number = rows.reduce((total: number, row: IMonthlyOptionRow) => {
            if (row.product_level_value.toLowerCase() !== 'total') {
                const monthlySum: number = Object.entries(row.sku_plans)
                    .filter(([key]: [string, number]) => !['sku_plan', 'Total'].includes(key))
                    .reduce((sum: number, [_, value]: [string, number]) => sum + (value || 0), 0);
                return total + monthlySum;
            }
            return total;
        }, 0);

        // Return the difference between total sku_plan and sum of monthly values
        return totalSkuPlan - totalMonthlyValues;
    }

    public calculateGrandTotal(rows: Array<IMonthlyOptionRow>): number {
        let grandTotal: number = 0;

        if (rows) {
            for (const row of rows) {
                if (row.product_level_value !== 'Total') {
                    const skuPlanValues: Array<number> = Object.values(row.sku_plans).splice(
                        0,
                        Object.values(row.sku_plans).length - 2
                    );
                    const skuPlanSum: number = skuPlanValues.reduce((skuOne: number, skuTwo: number) => skuOne + skuTwo, 0);

                    grandTotal += Number(skuPlanSum);
                }
            }
        }

        return grandTotal;
    }

    public skusAreSetToPreviouslyGeneratedDefaultValues(newRow: IMonthlyOptionRow): boolean {
        if (this.previousData) {
            const oldRow: IMonthlyOptionRow = this.previousData.rows.find(
                (previousRow: IMonthlyOptionRow) => previousRow.product_level_value === newRow.product_level_value
            );

            if (oldRow) {
                const prevGeneratedDefaultRow: IMonthlyOptionRow = this.mapsCalculationService.calculateAndTruncateRowSkus(
                    oldRow,
                    this.data.columns
                );
                // eslint-disable-next-line
                for (const key in newRow.sku_plans) {
                    // if all of the previously generated default sku values are equal to the new
                    // sku values, it means that the user didn't make any changes to the row's
                    // sku values. However, if there's at least one difference, return false because the
                    // user did make at least one change to the skus after the row's skus were auto populated
                    if (
                        key !== 'Total' &&
                        key !== 'sku_plan' &&
                        prevGeneratedDefaultRow.sku_plans[key] !== newRow.sku_plans[key]
                    ) {
                        return false;
                    }
                }
            }
        }

        return true;
    }


    public initializeProductLevelSkus(): void {
        if (this.data) {
            for (const row of this.data.rows) {
                if (
                    row.product_level_value.toLowerCase() !== 'total' &&
                    row.product_level_value.toLowerCase() !== 'sku_plan' &&
                    (this.mapsCalculationService.allSkusAreZero(row) || this.skusAreSetToPreviouslyGeneratedDefaultValues(row))
                ) {
                    const calculatedRow: IMonthlyOptionRow = this.mapsCalculationService.calculateAndTruncateRowSkus(
                        row,
                        this.data.columns
                    );

                    if (calculatedRow) {
                        // Edit the row sku_plans directly so that the changes made to the data variable
                        // reflects in the parent component that passed the data variable to this child component
                        Object.keys(calculatedRow.sku_plans).forEach((key: string) => {
                            row.sku_plans[key] = calculatedRow.sku_plans[key];
                        });
                    }
                }
            }
        }
    }

    public updateTotalRow(): void {
        if (this.data) {
            const totalRow: IMonthlyOptionRow = this.data.rows.find(
                (row: IMonthlyOptionRow) => row.product_level_value.toLowerCase() === 'total'
            );

            if (totalRow) {
                // Sum all of the skus for each column specific column and update the associated
                // sku in the total row sku plans
                Object.keys(totalRow.sku_plans).forEach((key: string) => {
                    totalRow.sku_plans[key] = this.data.rows.reduce((acc: number, row: IMonthlyOptionRow) => {
                        if (row.product_level_value.toLowerCase() !== 'total') {
                            return acc + row.sku_plans[key];
                        } else {
                            return acc;
                        }
                    }, 0);
                });
            }
        }
    }

    public getShortId(id: string): string {
        return this._optionsApprovalService.getShortId(id);
    }
}
